import { useEffect, useRef } from "react";

export const MONITOR_POLL_MS = 30_000;

/**
 * Calls `poll` every `intervalMs` while the tab is visible. Hidden tabs skip ticks, and showing the tab
 * polls at once so a returning viewer never waits a full interval. The latest `poll` is always used, so
 * callers need not memoize it and changing it does not reset the timer.
 */
export default function useVisiblePoll(poll: () => unknown, intervalMs: number = MONITOR_POLL_MS): void {
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (!cancelled) timer = setTimeout(tick, intervalMs);
    };
    async function tick() {
      if (!document.hidden) await pollRef.current();
      schedule();
    }
    const onVisibilityChange = () => {
      if (document.hidden || cancelled) return;
      if (timer) clearTimeout(timer);
      void tick();
    };
    schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [intervalMs]);
}
